import { useEffect } from "react";
import { createFileRoute, Outlet, Navigate, useNavigate, useRouterState } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth-context";
import { AppShell } from "@/components/app/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useColorThemeFetcher } from "@/hooks/use-color-theme";
import {
  modSectionEnabled,
  sectionForPath,
  type ModeratorPermissions,
} from "@/lib/moderator-permissions";

export const Route = createFileRoute("/_app")({ component: AppLayout });

function AppLayout() {
  const { loading, user, profile, role } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Fetch & cache color theme for the entire app (StatusPill etc. read from cache).
  useColorThemeFetcher();

  // Re-check profiles.status on every navigation; if disabled, send to /account-disabled.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("status")
        .eq("id", user.id)
        .maybeSingle();
      if (cancelled) return;
      if (data && data.status !== "active") {
        navigate({ to: "/account-disabled" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, profile?.status, navigate]);

  if (loading)
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" />;
  if (profile && profile.status !== "active") return <Navigate to="/account-disabled" />;
  // MFA gate
  if (profile?.mfa_enabled) {
    const passed =
      typeof window !== "undefined" && sessionStorage.getItem("mfa_passed") === "1";
    const onChallenge =
      typeof window !== "undefined" && window.location.pathname === "/mfa-challenge";
    if (!passed && !onChallenge) return <Navigate to="/mfa-challenge" />;
  }
  // Moderator section route guard: if a section toggle is OFF, redirect away.
  if (role === "moderator" && profile) {
    const sec = sectionForPath(pathname);
    if (sec) {
      const perms = (profile.moderator_permissions ?? {}) as ModeratorPermissions;
      if (!modSectionEnabled(perms, sec)) return <Navigate to="/dashboard" />;
    }
  }
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

